import { useEffect, useRef } from 'react'
import { webSocketWorkerEvent } from '../../../services'
import { changeUtcTimeToLocal, cleanEmptyField, paginationHandler } from '../../../helper'
import { DATE_FORMAT } from '../../../utils/constants'
import { useCallback } from 'react'

const useTradeBlotterRequestHandler = ({
  gridScrollHight,
  tradeBlotterResponse,
  selected,
  disabled,
  page,
  tradesGroup,
  dealsGroup,
  unAllocatedDeals,
  filters,
  review,
  tradesGiveUp,
  setTradesGiveUp,
  tradesConversion,
  setTradesConversion,
  setReview,
  skeltonLoader,
  setTradeBlotterResponse,
  bloombergRefresh,
  setBloombergRefresh,
  setLoading,
  setDisabled,
  expandsRow,
  gridRef,
  dealTradesGroup,
  getWidgets,
  pagination,
  setLock,
  setPagination,
  setDealsGroup,
  setTradesGroup,
  setDealsGiveUpGroup,
  setDealTradesGroup,
  setDealsNotSentToPb,
  selectedAllDeal,
  setSelectedAllDeal,
  setSelected,
  setUnAllocatedDeals,
  dispatchNotification,
  tradeBlotterDataAgGrid,
  priceRatesNormalize,
  tradeBlotterPendingAgGrid,
  setTotalPage,
  totalPage
}) => {
  const dealsIdRefresh = useRef([])

  useEffect(() => {
    getWidgets()
  }, [])

  const handelTradeBlotterRefreshing = () => {
    if (gridRef.current && gridRef.current.api && 'refreshServerSide' in gridRef.current.api) {
      if (dealsIdRefresh.current.length) {
        for (const dealId of dealsIdRefresh.current) {
          if (tradeBlotterDataAgGrid.current.length) {
            const dealData = tradeBlotterDataAgGrid.current.find(
              deal => Array.isArray(deal.children) && +deal.id === +dealId
            )
            const rowNode = gridRef.current.api.getRowNode(`${dealId}D`)
            const wasExpanded = rowNode?.expanded

            if (rowNode && dealData) {
              rowNode.setData(dealData)
            }
            if (wasExpanded) {
              setTimeout(() => rowNode.setExpanded(true), 100)
            }
          }

          gridRef.current.api.refreshServerSide({
            purge: true,
            route: [String(dealId)]
          }) // Refresh the grid
        }
      }

      dealsIdRefresh.current = []
      const resized = localStorage.getItem('resized')
      if (resized !== 'true') {
        const calls = gridRef.current.columnApi.getAllGridColumns().map(col => col.getColId())
        gridRef.current.columnApi.autoSizeColumns(calls)
      }
      gridRef.current.api.refreshServerSide({ purge: false }) // Refresh the grid
    }
  }

  const insertItemsToGrid = newItems => {
    const currentDateTime = changeUtcTimeToLocal(
      new Date().toISOString(),
      DATE_FORMAT.BLOTTER_DATE_TIME
    )
    if (!tradeBlotterDataAgGrid.current || !Array.isArray(tradeBlotterDataAgGrid.current)) return

    const isAtTop = gridScrollHight.current?.top < 200
    const items = isAtTop ? [...newItems, ...tradeBlotterPendingAgGrid.current] : newItems

    if (isAtTop) {
      const insertions = [] // { item, index }
      for (const item of items) {
        const t = new Date(item.trade_date).getTime()
        let idx = tradeBlotterDataAgGrid.current.findIndex(
          r => new Date(r.trade_date).getTime() < t
        )
        if (idx === -1) idx = tradeBlotterDataAgGrid.current.length
        insertions.push({ item, index: idx })
        tradeBlotterDataAgGrid.current.splice(idx, 0, item)
      }

      tradeBlotterPendingAgGrid.current = []
      insertions
        .sort((a, b) => b.index - a.index)
        .forEach(ins => {
          gridRef.current.api.applyServerSideTransactionAsync(
            { add: [ins.item], addIndex: ins.index },
            result => {
              console.log('Async transaction result:', result)
              console.log(
                'Added',
                tradeBlotterPendingAgGrid.current.concat(items).length,
                'new deal(s) at',
                currentDateTime
              )
            }
          )
        })
      setBloombergRefresh(prev => prev + 1)
    } else {
      tradeBlotterPendingAgGrid.current = [...newItems, ...tradeBlotterPendingAgGrid.current].sort(
        (a, b) => new Date(b.trade_date).getTime() - new Date(a.trade_date).getTime()
      )
    }
  }

  const handelReconnected = useCallback(filters => {
    const copyFilters = JSON.parse(JSON.stringify(filters))
    cleanEmptyField(copyFilters)
    setTimeout(() => {
      webSocketWorkerEvent.sendEvent({ type: 'add_trade_blotter_session', data: copyFilters })
      // re-request the blotter so a reconnect (or a request lost on a dying socket) can never strand the grid empty
      webSocketWorkerEvent.sendEvent({ type: 'get_trade_blotter', data: copyFilters })
    }, 500)
  }, [])

  useEffect(() => {
    setTimeout(() => handelTradeBlotterRefreshing(), 100)
  }, [tradeBlotterResponse.data])

  useEffect(() => {
    const handelMessage = message => {
      if (message.data.status === 'error' && message.data.data === "user don't have permission") {
        return dispatchNotification("you don't have booking permission", 'error')
      }
      if (
        message.data.type === 'get_user_view' &&
        message.data.status === 'ok' &&
        message.data.data.view_was_changed === true
      ) {
        const copyFilters = JSON.parse(JSON.stringify(filters))
        cleanEmptyField(copyFilters)
        skeltonLoader()
        webSocketWorkerEvent.sendEvent({ type: 'get_trade_blotter', data: copyFilters })
      }

      if (
        message.data.type === 'create_deal' &&
        message.data.status === 'ok' &&
        message.data.data
      ) {
        const { data: deal } = message.data

        deal.trade_date = changeUtcTimeToLocal(deal.trade_date, DATE_FORMAT.BLOTTER_DATE_TIME)
        for (const trade of deal.children || []) {
          trade.trade_date = changeUtcTimeToLocal(trade.trade_date, DATE_FORMAT.BLOTTER_DATE_TIME)
        }

        // Add all new deals to the data array
        insertItemsToGrid([deal])
      }

      if (message.data.type === 'create_deal_report_lucera_broadcast') {
        // Handle the create_deal_report_lucera_broadcast message
        setLock(() => true)
        console.log(
          'Received create_deal_report_lucera_broadcast message with',
          message.data.data?.length || 1,
          'deal(s)'
        )

        // Handle both single deal (old format) and batched deals (new format)
        const deals = message.data.data

        if (deals.length === 0) {
          setLock(() => false)
          return true
        }

        for (const deal of deals) {
          deal.trade_date = changeUtcTimeToLocal(deal.trade_date, DATE_FORMAT.BLOTTER_DATE_TIME)
          for (const trade of deal.children || []) {
            trade.trade_date = changeUtcTimeToLocal(trade.trade_date, DATE_FORMAT.BLOTTER_DATE_TIME)
          }
        }
        // Add all new deals to the data array
        insertItemsToGrid(deals)

        setLock(() => false)
        return true
      }

      if (message.data.type === 'ws_reconnected') {
        handelReconnected(filters)
      }
      if (
        message.data.type === 'get_trade_blotter' &&
        message.data.status === 'ok' &&
        message.data.data &&
        Array.isArray(message.data.data.tradeBlotterData)
      ) {
        let { status, data } = message.data
        const { tradeBlotterData, filters, max_deals, review, total_page } = data
        if (tradeBlotterData.length === 0) {
          tradeBlotterDataAgGrid.current = []
          tradeBlotterPendingAgGrid.current = []
          setTradeBlotterResponse({
            status,
            data: tradeBlotterData
          })
          setReview(0)
          setLoading(false)
          setTotalPage(1)
          handelTradeBlotterRefreshing()
          paginationHandler(tradeBlotterData, page, { setDisabled, disabled })
          return
        }
        setTradeBlotterResponse({
          status,
          data: tradeBlotterData
        })
        tradeBlotterPendingAgGrid.current = []
        setPagination(requests => {
          requests[page] = filters
          return requests
        })
        setReview(review)
        setTotalPage(total_page)
        setSelectedAllDeal({ ...selectedAllDeal, maxDeals: max_deals })
        for (const element of tradeBlotterData) {
          element.trade_date = changeUtcTimeToLocal(
            element.trade_date,
            DATE_FORMAT.BLOTTER_DATE_TIME
          )
          if (Array.isArray(element.children)) {
            for (const trade of element.children) {
              trade.trade_date = changeUtcTimeToLocal(
                trade.trade_date,
                DATE_FORMAT.BLOTTER_DATE_TIME
              )
            }
          }
        }
        paginationHandler(tradeBlotterData, page, { setDisabled, disabled })
        tradeBlotterDataAgGrid.current = tradeBlotterData
        handelTradeBlotterRefreshing()
        setLoading(false)
      }
      const SUCCESS = 'success'
      if (
        message.data.type === 'create_trade' &&
        message.data.status === 'ok' &&
        message.data.data
      ) {
        const { data: trade } = message.data
        dispatchNotification(`trade: ${trade.id} is being create`, SUCCESS)

        // Use current time for trade_date to ensure deals appear at the top
        trade.trade_date = changeUtcTimeToLocal(trade.trade_date, DATE_FORMAT.BLOTTER_DATE_TIME)

        insertItemsToGrid([trade])
      }

      if (
        message.data.type === 'update_single_trade' &&
        message.data.status === 'ok' &&
        message.data.data
      ) {
        const { data: trade } = message.data
        gridRef.current.api.applyServerSideTransactionAsync({ update: [trade] })
      }
      if (
        message.data.type === 'update_trade' &&
        message.data.status === 'ok' &&
        message.data.data.trade_id
      ) {
        const id = Number(message.data.data.trade_id)
        dispatchNotification(`trade: ${id} is being update`, SUCCESS)
        if (id in tradesGroup || id in dealTradesGroup) {
          setSelected(select => select - 1)
        }

        setTradesGroup(lastTradeGroups => {
          delete lastTradeGroups[id]
          return lastTradeGroups
        })
        setDealTradesGroup(lastTradeGroups => {
          delete lastTradeGroups[id]
          return lastTradeGroups
        })
        setTradesConversion(lastTradesConversion => {
          delete lastTradesConversion[id]
          return lastTradesConversion
        })
      }

      if (
        message.data.type === 'update_deal' &&
        message.data.status === 'ok' &&
        message.data.data
      ) {
        const { data: deal } = message.data
        dealsIdRefresh.current.push(deal.id)
        const index = tradeBlotterDataAgGrid.current.findIndex(
          item => Number(item.id) === Number(deal.id) && Array.isArray(item.children)
        )
        if (deal.staging_status !== 'Live' || !['NEW', null].includes(deal.status)) {
          setReview(last => last + 1)
        }

        if (index !== -1) {
          gridRef.current.api.applyServerSideTransactionAsync({
            remove: [tradeBlotterDataAgGrid.current[index]]
          })
          tradeBlotterDataAgGrid.current.splice(index, 1, deal)
          gridRef.current.api.applyServerSideTransactionAsync({ add: [deal], addIndex: index })
        }
      }
      if (
        message.data.type === 'allocate_deal' &&
        message.data.status === 'ok' &&
        message.data.data.deals_id
      ) {
        let counter_review_deal = 0
        const dealsId = message.data.data.deals_id
        dispatchNotification(`deal: ${dealsId.join(',')} is being allocated`, SUCCESS)

        if (dealsId.length > 1) {
          for (const dealId of dealsId) {
            const dealIndex = tradeBlotterDataAgGrid.current.findIndex(
              d => Number(d.id) === Number(dealId)
            )
            if (dealIndex !== -1) {
              counter_review_deal++
              const deal = tradeBlotterDataAgGrid.current[dealIndex]
              tradeBlotterDataAgGrid.current.splice(dealIndex, 1)
              gridRef.current.api.applyServerSideTransactionAsync({ remove: [deal] })
            }
          }
        }
        setReview(last => last - counter_review_deal)
        setUnAllocatedDeals(lastUnAllocateDeal => {
          for (const dealId of dealsId) {
            const id = Number(dealId)
            if (id in lastUnAllocateDeal) {
              delete lastUnAllocateDeal[id]
            }
          }
          return lastUnAllocateDeal
        })

        if (dealsId.length > 1) {
          setDealsGroup(lastDealGroup => {
            let counter = 0
            for (const dealId of dealsId) {
              const id = Number(dealId)
              if (id in lastDealGroup) {
                delete lastDealGroup[id]
                counter++
              }
            }
            setSelected(select => select - counter)
            return lastDealGroup
          })

          setDealsNotSentToPb(lastDealsNotSentToPb => {
            for (const dealId of dealsId) {
              const id = Number(dealId)
              if (id in lastDealsNotSentToPb) {
                delete lastDealsNotSentToPb[id]
              }
            }
            return lastDealsNotSentToPb
          })

          setDealsGiveUpGroup(lastDealGiveUp => {
            for (const dealId of dealsId) {
              const id = Number(dealId)
              if (id in lastDealGiveUp) {
                delete lastDealGiveUp[id]
              }
            }
            return lastDealGiveUp
          })
        }
      }
      if (message.data.type === 'delete_trades_or_deals' && message.data.status === 'ok') {
        let counter_review_trade = 0
        let counter_review_deal = 0
        if (Array.isArray(message.data.data.trades_id) && message.data.data.trades_id.length > 0) {
          const { trades_id } = message.data.data
          dispatchNotification(`trades: ${trades_id.join(',')} is being delete`, SUCCESS)
          for (const tradeId of trades_id) {
            const trade = tradeBlotterDataAgGrid.current.find(
              trade => Number(trade.id) === Number(tradeId) && !Array.isArray(trade.children)
            )
            if (trade) {
              counter_review_trade++
              gridRef.current.api.applyServerSideTransactionAsync({ remove: [trade] })
            }
          }

          setDealTradesGroup(lastTradeGroups => {
            let counter = 0
            for (const tradeId of trades_id) {
              const id = Number(tradeId)
              if (id in lastTradeGroups) {
                counter++
                delete lastTradeGroups[id]
              }
            }
            setSelected(select => select - counter)
            return lastTradeGroups
          })

          setTradesGroup(lastTradesGroup => {
            let counter = 0
            for (const tradeId of trades_id) {
              const id = Number(tradeId)
              if (id in lastTradesGroup) {
                counter++
                delete lastTradesGroup[id]
              }
            }
            setSelected(select => select - counter)
            return lastTradesGroup
          })

          setTradesConversion(lastTradesConversion => {
            let counter = 0
            for (const tradeId of trades_id) {
              const id = Number(tradeId)
              if (id in lastTradesConversion) {
                counter++
                delete lastTradesConversion[id]
              }
            }
            setSelected(select => select - counter)
            return lastTradesConversion
          })

          setTradesGiveUp(lastTradesGiveUp => {
            let counter = 0
            for (const tradeId of trades_id) {
              const id = Number(tradeId)
              if (id in lastTradesGiveUp) {
                counter++
                delete lastTradesGiveUp[id]
              }
            }
            setSelected(select => select - counter)
            return lastTradesGiveUp
          })
        }

        if (Array.isArray(message.data.data.deals_id) && message.data.data.deals_id.length > 0) {
          const { deals_id } = message.data.data
          dispatchNotification(`deals: ${deals_id.join(',')} is being delete`, SUCCESS)
          for (const dealId of deals_id) {
            const dealIndex = tradeBlotterDataAgGrid.current.findIndex(
              deal => Number(deal.id) === Number(dealId) && Array.isArray(deal.children)
            )

            if (dealIndex !== -1) {
              const deal = tradeBlotterDataAgGrid.current[dealIndex]
              dealsIdRefresh.current.push(Number(dealId))
              if (deal.staging_status !== 'Live' || !['NEW', null].includes(deal.status)) {
                counter_review_deal++
              }
              deal.is_active = 0
              deal.pnl_usd_mtm = null
              deal.status = 'DELETE'
              deal.is_delete = 1
              deal.children = deal.children.map(t => ({ ...t, is_delete: 1 }))
              gridRef.current.api.applyServerSideTransactionAsync({ update: [deal] })
            }
          }
          if (dealsIdRefresh.current.length) {
            setTradeBlotterResponse({ data: tradeBlotterDataAgGrid.current, status: 'ok' })
            handelTradeBlotterRefreshing()
          }
          setDealsGroup(lastDealGroup => {
            let counter = 0
            for (const dealId of deals_id) {
              const id = Number(dealId)
              if (id in lastDealGroup) {
                delete lastDealGroup[id]
                counter++
              }
            }
            setSelected(select => select - counter)
            return lastDealGroup
          })

          setUnAllocatedDeals(lastUnAllocateDeal => {
            for (const deal_id of deals_id) {
              if (deal_id in lastUnAllocateDeal) {
                delete lastUnAllocateDeal[deal_id]
              }
            }
            return lastUnAllocateDeal
          })

          setDealsNotSentToPb(lastDealsNotSentToPb => {
            for (const deal_id of deals_id) {
              const id = Number(deal_id)
              if (id in lastDealsNotSentToPb) {
                delete lastDealsNotSentToPb[id]
              }
            }
            return lastDealsNotSentToPb
          })

          setDealsGiveUpGroup(lastDealGiveUp => {
            for (const dealId of deals_id) {
              const id = Number(dealId)
              if (id in lastDealGiveUp) {
                delete lastDealGiveUp[id]
              }
            }
            return lastDealGiveUp
          })
        }
        if (counter_review_trade || counter_review_deal) {
          setReview(last => last - counter_review_trade - counter_review_deal)
        }
        if (counter_review_trade && totalPage > 1) {
          const copyFilters = JSON.parse(JSON.stringify(filters))
          cleanEmptyField(copyFilters)
          webSocketWorkerEvent.sendEvent({ type: 'get_trade_blotter', data: copyFilters })
        }
      }
      if (
        message.data.type === 'group_trades' &&
        message.data.status === 'ok' &&
        Array.isArray(message.data?.data?.trades_id)
      ) {
        let tradesCounter = 0
        const { trades_id } = message.data.data
        const tradesId = trades_id.map(tradeId => Number(tradeId))
        for (const tradeId of tradesId) {
          const trade = tradeBlotterDataAgGrid.current.find(
            t => Number(t.id) === Number(tradeId) && !Array.isArray(t.children)
          )
          if (trade) {
            tradesCounter++
            gridRef.current.api.applyServerSideTransactionAsync({ remove: [trade] })
          }
        }
        dispatchNotification(`trades: ${tradesId.join(',')} are being group to a deal`, SUCCESS)
        setTradesGroup(lastTradeGroups => {
          let counter = 0
          for (const tradeId of trades_id) {
            const id = Number(tradeId)
            if (id in lastTradeGroups) {
              counter++
              delete lastTradeGroups[id]
            }
          }
          setSelected(select => select - counter)
          return lastTradeGroups
        })
        setReview(last => last - tradesId.length)
        if (tradesCounter && totalPage > 1) {
          const copyFilters = JSON.parse(JSON.stringify(filters))
          cleanEmptyField(copyFilters)
          webSocketWorkerEvent.sendEvent({ type: 'get_trade_blotter', data: copyFilters })
        }
      }
      if (
        message.data.type === 'un_group_deals' &&
        message.data.status === 'ok' &&
        Array.isArray(message.data?.data?.deals_id)
      ) {
        const { deals_id } = message.data.data
        const dealsId = deals_id.map(dealId => Number(dealId))
        dispatchNotification(`deals: ${dealsId.join(',')} is/are being ungrouped`, SUCCESS)
        for (const dealId of dealsId) {
          const deal = tradeBlotterDataAgGrid.current.find(d => Number(d.id) === Number(dealId))
          if (deal) {
            gridRef.current.api.applyServerSideTransactionAsync({ remove: [deal] })
          }
        }
        setDealsGroup(lastDealGroups => {
          let counter = 0
          for (const id of dealsId) {
            if (id in lastDealGroups) {
              counter++
              delete lastDealGroups[id]
            }
          }
          setSelected(select => select - counter)
          return lastDealGroups
        })
        setDealsNotSentToPb(lastDealsNotSentToPb => {
          for (const id of dealsId) {
            if (id in lastDealsNotSentToPb) {
              delete lastDealsNotSentToPb[id]
            }
          }
          return lastDealsNotSentToPb
        })

        setUnAllocatedDeals(lastUnAllocateDeal => {
          for (const deal_id of deals_id) {
            if (deal_id in lastUnAllocateDeal) {
              delete lastUnAllocateDeal[deal_id]
            }
          }
          return lastUnAllocateDeal
        })

        setDealsGiveUpGroup(lastDealGiveUpGroup => {
          for (const id of dealsId) {
            delete lastDealGiveUpGroup[id]
          }
          return lastDealGiveUpGroup
        })
      }
      if (
        message.data.type === 'give_up_deals' &&
        message.data.status === 'ok' &&
        (Array.isArray(message.data?.data?.deals_id) ||
          Array.isArray(message.data?.data?.trades_id))
      ) {
        const { deals_id = [], trades_id = [] } = message.data.data
        const dealsId = deals_id.map(dealId => Number(dealId))
        const tradesId = trades_id.map(tradeId => Number(tradeId))
        if (dealsId.length) {
          dispatchNotification(`deals: ${dealsId.join(',')} is/are being Live`, SUCCESS)
        }
        if (tradesId.length) {
          dispatchNotification(`trades:${tradesId.join(',')} is/are being conversion/s`)
        }
        for (const dealId of deals_id) {
          const rowNode = gridRef.current.api.getRowNode(`${dealId}D`)
          if (!rowNode) continue

          const updatedDeal = {
            ...rowNode.data,
            status: null,
            staging_status: 'Live'
          }
          if (filters.status_warning_and_union) {
            gridRef.current.api.applyServerSideTransactionAsync({ remove: [rowNode.data] })
          } else {
            rowNode.setData(updatedDeal)
            gridRef.current.api.applyServerSideTransactionAsync({ update: [updatedDeal] })
          }
        }
        for (const tradeId of trades_id) {
          const rowNode = gridRef.current.api.getRowNode(`${tradeId}T`)
          if (!rowNode) continue

          const updatedTrade = {
            ...rowNode.data,
            status: null,
            staging_status: 'Live'
          }

          if (filters.status_warning_and_union) {
            gridRef.current.api.applyServerSideTransactionAsync({ remove: [rowNode.data] })
          } else {
            rowNode.setData(updatedTrade)
            gridRef.current.api.applyServerSideTransactionAsync({ update: [updatedTrade] })
          }
        }

        if (dealsId.length) {
          setDealsGroup(lastDealGroups => {
            for (const id of dealsId) {
              if (id in lastDealGroups) {
                delete lastDealGroups[id]
              }
            }
            return lastDealGroups
          })

          setDealsGiveUpGroup(lastDealGiveUpGroup => {
            let counter = 0
            for (const id of dealsId) {
              counter++
              delete lastDealGiveUpGroup[id]
            }
            setReview(r => r - counter)
            setSelected(select => select - counter)
            return lastDealGiveUpGroup
          })
          setDealsNotSentToPb(lastDealNotSentToPb => {
            for (const id of dealsId) {
              delete lastDealNotSentToPb[id]
            }
            return lastDealNotSentToPb
          })
        }
        if (tradesId.length) {
          setTradesGiveUp(lastTradesGiveUp => {
            let counter = 0
            for (const id of tradesId) {
              if (id in lastTradesGiveUp) {
                counter++
                delete lastTradesGiveUp[id]
              }
            }
            setReview(r => r - counter)
            return lastTradesGiveUp
          })
        }
        if (
          (tradesId.length || dealsId.length) &&
          filters.status_warning_and_union &&
          totalPage > 1
        ) {
          const copyFilters = JSON.parse(JSON.stringify(filters))
          cleanEmptyField(copyFilters)
          webSocketWorkerEvent.sendEvent({ type: 'get_trade_blotter', data: copyFilters })
        }
      }
    }

    webSocketWorkerEvent.addEventListener('message', handelMessage)
    return () => webSocketWorkerEvent.removeEventListener('message', handelMessage)
  }, [
    webSocketWorkerEvent,
    gridScrollHight,
    pagination,
    gridRef,
    dealsIdRefresh,
    filters,
    tradesGiveUp,
    unAllocatedDeals,
    tradesConversion,
    tradeBlotterResponse,
    selected,
    disabled,
    page,
    expandsRow,
    bloombergRefresh,
    selectedAllDeal,
    tradesGroup,
    dealsGroup,
    review,
    priceRatesNormalize
  ])
}

export default useTradeBlotterRequestHandler
